// 週期的花 —— **五個橢圓瓣的並集輪廓**（W58.6 · ADR-0213）。
//
// **這是第三次重畫這朵花，而這一次的理由跟前兩次不同。**
//
// | 版本 | 決策 | 使用者的反饋 |
// |---|---|---|
// | W58.0 | lucide `Flower2` | 「內部圓點過多，縮小後像麻將一筒或蓮蓬頭」 |
// | W58.5 | 自繪**單層五瓣剪影**（ADR-0209） | 「看起來像**尖角的五角星 ★**」 |
// | **W58.6** | **五個橢圓瓣的並集輪廓**（本檔） | — |
//
// **W58.5 的診斷是對的，解法只對了一半。** ADR-0209 說「內部線條在小尺寸是雜訊」——
// 這條完全正確，所以它清掉了內部點陣。但它**沒有處理外輪廓本身的轉角**，而那才是
// 五角星的成因：
//
// ADR-0209 的形狀用**二次貝茲曲線**畫瓣（瓣尖半徑 9.0、瓣谷半徑 4.6）。瓣谷處
// 相鄰兩段曲線**沒有切線連續**，轉角約 **101°** —— 10px 之下，那個硬轉角就是
// 五角星的尖。實測：把那條 path 渲染在 10 / 12px 下，讀出來就是 ★。
//
// **所以這一輪的約束不是「少幾個圖元」，是「輪廓上沒有硬轉角」。**
//
// ---
//
// ## 幾何：5 個橢圓瓣的**並集**
//
// | 元素 | 參數 |
// |---|---|
// 橢圓瓣 | `rx = 5.6`、`ry = 3.9` |
// 瓣心距花心 | `3.6` |
// 瓣的方位 | 每 `72°` 一個，起始 `-90°` |
// 瓣尖半徑 | `3.6 + 5.6 = 9.2`（橢圓的**圓端**，不是尖角） |
// 瓣谷半徑 | `6.37`（相鄰兩瓣橢圓弧的交點） |
//
// 瓣的長軸沿**徑向**，所以瓣尖是橢圓的圓端 —— **沒有尖角**。花瓣之間是兩條
// 橢圓弧交出來的**凹**角，那是花瓣分界；五角星的尖是**凸**的，方向正好相反。
//
// ## 為什麼是「並集」而不是「5 個橢圓」
//
// **這是結構要求，不是美學偏好。** 空心版若由「5 個橢圓各自描邊」構成，
// 花瓣會互相穿插出**內部線** —— 那正是 W58.0 推翻 `Flower2` 的**同一個**缺陷。
// 所以必須是**單一條閉合輪廓**：實心 = `fill`，空心 = `stroke`，
// **兩者形狀完全相同**，不會漂移。
//
// ## 這條 path 是**算出來的**，不是手調的
//
// 1. 花心出發的射線與旋轉橢圓的交點半徑**有解析解**（二次方程）；
// 2. 相鄰兩瓣的交點 = 「兩條出射半徑相等」的那個角度（二分求解）；
// 3. 每瓣露在外面的那段邊界是**一段精確的橢圓弧** → 一條 SVG `A` 命令；
// 4. **驗收是數值的**：渲染後的 path 用 `getPointAtLength` 取 1440 個點，
//    與數值並集逐角度比較半徑，最大誤差 **0.002 / 24 單位**（0.008%）。
//
// （求解腳本在 `.scratch/flower-union.mjs`，不入庫 —— 它是產生這串數字的工具，
// 不是產品程式碼。）
//
// ## 實作成 lucide icon（`createLucideIcon`）
//
// 因為 `CATEGORY_ICON.cycle` 的型別是 `LucideIcon`，做成 lucide icon 意味著
// **六個既有消費者**（格子的 chip、篩選面板、事件表單、日詳情、圖例、週期頁的
// 歷史清單）**自動換成新花，一行都不用改**。
//
// root `AGENTS.md §3 #19` 寫「icon library only」。這裡沒有違背那條：
// `createLucideIcon` **就是 lucide 家族的一員** —— 同一個渲染器、同一個
// `size` / `strokeWidth` / `currentColor` 合約，只是自繪的圖元。

import { createLucideIcon } from "lucide-react";

/**
 * The one path every rendering of this flower uses.
 *
 * **一個字串，兩種畫法**（實心 / 空心）—— 所以「圖例的花」與「格子的花」
 * 不可能是兩個形狀。
 *
 * 五段 `A` = 五段橢圓弧，每段是**一個花瓣露在外面的那一段**；
 * 弧的端點就是相鄰兩瓣的交點（瓣谷）。
 */
export const CYCLE_FLOWER_PATH =
  "M 8.254 6.844 A 5.6 3.9 -90 0 1 15.746 6.844 A 5.6 3.9 -18 0 1 18.062 13.97 " +
  "A 5.6 3.9 54 0 1 12 18.374 A 5.6 3.9 126 0 1 5.938 13.97 A 5.6 3.9 198 0 1 8.254 6.844 Z";

/**
 * The flower, filled — **一塊實心的形狀**。
 *
 * `fill="currentColor"` + `stroke="none"`：lucide 的 `<svg>` 預設是
 * `fill="none" stroke="currentColor"`，所以這兩個屬性一定要在 path 上明寫，
 * 否則會變成一個只有輪廓的空心花。
 */
export const CycleFlower = createLucideIcon("CycleFlower", [
  ["path", { d: CYCLE_FLOWER_PATH, fill: "currentColor", stroke: "none" }],
]);

/**
 * The flower, hollow — **同一條路徑，只是描邊不填色**。
 *
 * **為什麼需要它。** 月曆格上「實際經期 vs 預測經期」與「排卵日 vs 排卵期」
 * 這兩組是**底色分不出來的**（W58.3 記錄過：`actual` 與 `predicted` 是 25% 與
 * 15% 的玫瑰，`ovulation-day` 與 `ovulation` **底色完全相同**）。分開它們的
 * 是**實心 / 空心**這條形狀通道 —— 以前由 marker 的**外層圓盤**講，現在
 * 圓盤沒了（角標只有一朵花），所以這條通道改由**花本身**講。
 *
 * **不是另一條路徑。** 用同一條 `d` 只是換填色，所以兩種畫法**形狀完全相同** ——
 * 「實心的那朵」與「空心的那朵」是同一朵花的兩種畫法，不會漂移。
 *
 * `strokeWidth: 2.2` —— ADR-0209 用過 `2.4`，那是給「五瓣各自的邊」准备的。
 * 單一輪廓的邊比那種簡潔，`2.4` 在 10px 下會讓**瓣谷的口糊住**（谷口在
 * 14 單位上只有 2.4 寬，2.4 的線兩邊各吃 1.2，口就剩不下什麼了）。
 */
export const CycleFlowerHollow = createLucideIcon("CycleFlowerHollow", [
  [
    "path",
    {
      d: CYCLE_FLOWER_PATH,
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.2",
      strokeLinejoin: "round",
    },
  ],
]);
